import qz from "qz-tray";
import { Secrets } from "types";
import { queryClient } from "../api/queryClient";
import { getDefaultPrinter, getTicketPrinter, shouldUseBrowserPrintDialog } from "../storages/PrinterStorage";

export enum PrintType {
    RECEIPT = "receipt",
    TICKET = "ticket",
}

export const getPrinters = async (secrets: Secrets) => {
    await setCertificates(secrets);

    try {
        await qz.websocket.connect();
        console.log("QZ: Connected");

        const printers = await qz.printers.find();
        console.log("QZ: Found printers", printers);

        qz.websocket.disconnect();
        return printers;
    } catch {}
};

export const setCertificates = async (secrets: Secrets) => {
    if (!secrets.qz?.certificate || !secrets.qz.privateKey) {
        throw new Error("QZ certificate & private key not available. Check if the API returns certificates");
    }

    const { privateKey, certificate } = secrets.qz;

    // Example from: https://github.com/qzind/tray/blob/master/assets/signing/sign-message.js

    qz.security.setCertificatePromise((resolve: (certificate: string) => void) => {
        resolve(certificate);
    });

    qz.security.setSignatureAlgorithm("SHA512");

    qz.security.setSignaturePromise((toSign: string) => async (resolve: (signature: string) => void) => {
        const { KEYUTIL, KJUR, stob64, hextorstr } = await import("jsrsasign"); // Using dynamic import since the library is big
        const signature = new KJUR.crypto.Signature({ alg: "SHA512withRSA" });
        signature.init(KEYUTIL.getKey(privateKey));
        signature.updateString(toSign);
        const hex = signature.sign();
        resolve(stob64(hextorstr(hex)));
    });
};

/**
 * @param content Content to print
 * @param codes Additional HEX code to send to the printer after printing
 * @param printType Type of printing: receipt or ticket
 */
export const print = async (content: string, codes: string[] = [], printType: string = PrintType.RECEIPT) => {
    console.group("QZ");

    const defaultPrinter = getDefaultPrinter();
    const ticketPrinter = getTicketPrinter();

    const useQz =
        !shouldUseBrowserPrintDialog() &&
        ((printType === PrintType.TICKET && !!ticketPrinter) || (printType === PrintType.RECEIPT && !!defaultPrinter));

    if (useQz) {
        const { secrets } = queryClient.getQueryData<{ secrets: Secrets }>(["account"])!;

        await setCertificates(secrets).catch((error: any) => {
            console.error("Setting certificates failed", error);
        });

        await qz.websocket.connect().catch((error: any) => {
            console.error("Connect failed", error);
        });

        const printerToUse = printType === PrintType.TICKET ? ticketPrinter : defaultPrinter;
        // Had to disable the check, not sure why eslint thinks `printerToUse` is a function
        // eslint-disable-next-line unicorn/no-array-callback-reference
        const printer = await qz.printers.find(printerToUse).catch((error: any) => {
            console.error("Finding printer failed", error);
        });

        if (printer) {
            const config = qz.configs.create(printer);
            const data =
                printType === PrintType.TICKET
                    ? [content]
                    : [{ type: "pixel", format: "html", flavor: "plain", data: content }];

            console.log("Print data", data);
            if (content === "") {
                console.log("Executing codes");
                await printCodes(config, codes);
            } else {
                await qz
                    .print(config, data)
                    .then(async () => {
                        if (printType === PrintType.TICKET) {
                            return;
                        }

                        await printCodes(config, codes);
                    })
                    .then(() => {
                        console.log("Print Job Successful");
                    })
                    .catch((error: any) => {
                        console.error("Print failed", error);
                    });
            }
        }

        await qz.websocket.disconnect().catch((error: any) => {
            console.error("Disconnect failed", error);
        });
    }

    // Fallback to browser print dialog
    if (printType === PrintType.RECEIPT && shouldUseBrowserPrintDialog() && content !== "") {
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "absolute";
        printFrame.style.width = "0";
        printFrame.style.height = "0";
        printFrame.style.border = "0";
        document.body.append(printFrame);

        const frameDocument = printFrame.contentWindow?.document;
        if (frameDocument) {
            frameDocument.write(content);
            frameDocument.close();
            printFrame.contentWindow?.focus();
            printFrame.contentWindow?.print();
        }
        printFrame.contentWindow?.addEventListener("afterprint", () => {
            printFrame.remove();
        });
    }

    console.groupEnd();
};

const printCodes = async (config: any, codes: string[]) => {
    const rawData = codes.map((code) => {
        return { type: "raw", flavor: "hex", data: code };
    });
    return qz
        .print(config, rawData)
        .then(() => {
            console.log("Codes executed successfully");
        })
        .catch((error: any) => {
            console.error("Codes execution failed", error);
        });
};
